import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { TransferList } from '@components/common/TransferList';
import styles from './RetailStoreConfigModal.module.css';
import { WarehousesReadmodelType, PriceListReadmodelType } from '@bett3r-dev/teselly-domain';

export interface RetailStoreConfigModalProps {
  opened: boolean;
  onClose: () => void;
  availableWarehouses: WarehousesReadmodelType[];
  availablePriceLists: PriceListReadmodelType[];
  warehouseIds: string[];
  defaultChannelWarehouseId: string | null;
  onWarehousesChange: ( selectedIds: string[], defaultId: string | null ) => void;
  priceListIds: string[];
  defaultPriceListId: string | null;
  onPriceListsChange: ( selectedIds: string[], defaultId: string | null ) => void;
}

const RetailStoreConfigModal = ({
  opened,
  onClose,
  availableWarehouses, availablePriceLists,
  warehouseIds, defaultChannelWarehouseId, onWarehousesChange,
  priceListIds, defaultPriceListId, onPriceListsChange
}: RetailStoreConfigModalProps ) => {
  const { t } = useTranslation( 'channels' );

  // Temporary state for the modal so we can cancel changes
  const [ tempWarehouseIds, setTempWarehouseIds ] = useState<string[]>( warehouseIds );
  const [ tempDefaultChannelWarehouseId, setTempDefaultChannelWarehouseId ] = useState<string | null>( defaultChannelWarehouseId );
  const [ tempPriceListIds, setTempPriceListIds ] = useState<string[]>( priceListIds );
  const [ tempDefaultPriceListId, setTempDefaultPriceListId ] = useState<string | null>( defaultPriceListId );

  // Sync temp state with props when modal opens
  useEffect(() => {
    if ( opened ) {
      setTempWarehouseIds( warehouseIds );
      setTempDefaultChannelWarehouseId( defaultChannelWarehouseId );
      setTempPriceListIds( priceListIds );
      setTempDefaultPriceListId( defaultPriceListId );
    }
  }, [ opened, warehouseIds, defaultChannelWarehouseId, priceListIds, defaultPriceListId ]);

  const handleAccept = () => {
    onWarehousesChange( tempWarehouseIds, tempDefaultChannelWarehouseId );
    onPriceListsChange( tempPriceListIds, tempDefaultPriceListId );
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      trapFocus={false}
      yOffset="10dvh"
      zIndex={300}
      title={<Title order={4} size={16}>{t( 'add_channel_modal_retail_config_modal_title', 'Depósitos y listas de precios' )}</Title>}
      classNames={{ content: styles.modal, body: styles.modalBody }}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
        <div>
          <ScrollArea h="50vh" type="auto" px="md" py="md">
            <Text size="sm" c="dimmed" mb="md">
              {t( 'add_channel_modal_retail_store_step2_desc', 'Puedes seleccionar qué depósitos y listas de precios estarán asociados a este canal. Si prefieres, puedes omitir este paso y configurarlos más adelante desde la vista de detalles.' )}
            </Text>
            <Stack gap="xl" style={{ width: '100%' }}>
              <div className={styles.sectionWrapper}>
                <Text size="xs" fw={600} mb={4} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                  {t( 'add_channel_modal_retail_store_warehouses', 'Depósitos' )}
                </Text>
                <Text size="11px" c="dimmed" mb={12}>
                  {t( 'transfer_list_instructions' )}
                </Text>
                <div className={styles.transferListWrapper}>
                  <TransferList
                    availableItems={availableWarehouses.map( w => ({ id: w.id, label: w.name }))}
                    selectedIds={tempWarehouseIds}
                    defaultId={tempDefaultChannelWarehouseId}
                    onChange={( ids, def ) => {
                      setTempWarehouseIds( ids );
                      setTempDefaultChannelWarehouseId( def );
                    }}
                    labels={{
                      available: t( 'transfer_list_available', 'Disponibles' ),
                      selected: t( 'transfer_list_selected', 'Seleccionados' ),
                      defaultTooltip: t( 'transfer_list_set_default', 'Establecer como predeterminado' )
                    }}
                  />
                </div>
              </div>
              <div className={styles.sectionWrapper}>
                <Text size="xs" fw={600} mb={4} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                {t( 'add_channel_modal_retail_store_pricelists', 'Listas de precios' )}
              </Text>
              <Text size="11px" c="dimmed" mb={12}>
                {t( 'transfer_list_instructions' )}
              </Text>
              <div className={styles.transferListWrapper}>
                <TransferList
                  availableItems={availablePriceLists.map( p => ({ id: p.id, label: p.name }))}
                  selectedIds={tempPriceListIds}
                  defaultId={tempDefaultPriceListId}
                  onChange={( ids, def ) => {
                    setTempPriceListIds( ids );
                    setTempDefaultPriceListId( def );
                  }}
                  labels={{
                    available: t( 'transfer_list_available', 'Disponibles' ),
                    selected: t( 'transfer_list_selected', 'Seleccionados' ),
                    defaultTooltip: t( 'transfer_list_set_default', 'Establecer como predeterminado' )
                  }}
                />
              </div>
            </div>
          </Stack>
        </ScrollArea>
        <div className={styles.footer}>
          <Group justify="flex-end">
            <Button variant="light" onClick={onClose} size="sm" fz="13px" fw={500}>
              {t( 'add_channel_modal_retail_config_back', 'Volver' )}
            </Button>
            <Button onClick={handleAccept} size="sm" fz="13px" fw={500}>
              {t( 'add_channel_modal_retail_config_accept', 'Aceptar' )}
            </Button>
          </Group>
        </div>
      </div>
    </Modal>
  );
};

export default RetailStoreConfigModal;
